/**
 * Audience segment tracking for landing-page CTA flows.
 *
 * When a visitor clicks a CTA tagged with `?audience=student` (or any
 * other segment), we want the visitor row to be stamped with that
 * segment so the admin can filter conversion data by intended
 * audience. This module is the single source of truth for reading the
 * URL parameter and persisting it across the visit.
 *
 * Storage strategy:
 *  - On any page load that has `?audience=` in the URL, capture the
 *    value into localStorage (`meinvent_audience`). Later pages can
 *    read it from localStorage even after the URL param falls off.
 *  - On subsequent reads, prefer the URL param (fresh signal — a
 *    visitor who clicks a different audience link should re-segment)
 *    and fall back to localStorage.
 *  - The backend writes "latest non-null wins" on the visitor row,
 *    so re-segmentation across visits is captured cleanly.
 *
 * Allowlist: we only persist values from a known set so a malicious
 * or accidental `?audience=<garbage>` doesn't pollute the data.
 */

const KEY = 'meinvent_audience';
const ALLOWED = new Set(['student', 'spark']);

/** Read `?audience=` from the current URL once and persist if allowed. */
export function capturePageLoadAudience(): void {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    const raw = url.searchParams.get('audience');
    if (!raw) return;
    if (!ALLOWED.has(raw)) return;
    localStorage.setItem(KEY, raw);
  } catch {
    /* localStorage / URL parsing may fail in restricted contexts — silently skip. */
  }
}

/**
 * Imperatively stamp the visitor as a member of an audience segment,
 * regardless of URL. Used by pages where the act of landing on the
 * page itself is the audience signal (e.g. `/students` — any visitor
 * who arrives there belongs in the student funnel). Without this,
 * visitors who type the URL directly or follow a non-tagged link
 * never get the localStorage flag set, and any outbound link from
 * the page that doesn't manually carry `?audience=` loses the
 * segment.
 *
 * Silently rejects values not in the ALLOWED set, same as
 * `capturePageLoadAudience`.
 */
export function stampAudience(audience: string): void {
  if (typeof window === 'undefined') return;
  if (!ALLOWED.has(audience)) return;
  try {
    localStorage.setItem(KEY, audience);
  } catch {
    /* localStorage may be unavailable in restricted contexts. */
  }
}

/**
 * Tab-scoped tracking of the most recent landing page the visitor was
 * on. Used by demo pages (sample memo, education center, etc.) to
 * decide where the "back" / wordmark link should route — back to
 * wherever the visitor came from in THIS tab's navigation flow, not
 * wherever they self-identified for analytics purposes (which is
 * sticky in localStorage and can be stale).
 *
 * Why sessionStorage, not localStorage:
 *   - localStorage persists across tabs and visits. If a visitor
 *     visited /students yesterday, their audience flag stays 'student'
 *     forever — but if they open a new tab today and start at /, the
 *     demo back-link should go to /, not /students.
 *   - sessionStorage clears on tab close, which matches the user's
 *     mental model of "where I came from."
 *
 * Allowlist mirrors the audience allowlist plus '/' for the main
 * landing — anything outside this set is ignored.
 */
const LANDING_KEY = 'meinvent_last_landing';
const LANDING_ALLOWED = new Set(['/', '/students']);

/**
 * Record that the visitor is currently on a landing-style page. Called
 * from each landing page's mount effect.
 */
export function markLandingVisit(page: string): void {
  if (typeof window === 'undefined') return;
  if (!LANDING_ALLOWED.has(page)) return;
  try {
    sessionStorage.setItem(LANDING_KEY, page);
  } catch {
    /* sessionStorage may be unavailable in restricted contexts. */
  }
}

/**
 * Read the most recently visited landing page in this tab, falling
 * back to '/' if none was recorded. Demo pages use this to decide
 * where the home/wordmark link should route.
 */
export function getLastLandingPage(): string {
  if (typeof window === 'undefined') return '/';
  try {
    const stored = sessionStorage.getItem(LANDING_KEY);
    return stored && LANDING_ALLOWED.has(stored) ? stored : '/';
  } catch {
    return '/';
  }
}

/* ---------------------------------------------------------------------------
 * Landing A/B variant ('control' | 'v2')
 *
 * WRITE-ONCE in localStorage — the FIRST landing a browser sees wins, and that
 * variant then rides every heartbeat and the signup, so the eventual conversion
 * is credited to the page that acquired the visitor (a later cross-visit to the
 * other variant must not reassign them). The backend also enforces write-once
 * on the visitor row as a second guard.
 * ------------------------------------------------------------------------- */
const VARIANT_KEY = 'meinvent_landing_variant';
/**
 * Every A/B landing variant a browser can be stamped with:
 *   control — the live dark homepage
 *   v2      — the light-minimalist homepage
 *   va–vg   — the seven design-direction variants (Calm / Proof / Editorial /
 *             App / Lab / Statement / Signal) served at /va … /vg for testing.
 */
export type LandingVariant = 'control' | 'v2' | 'va' | 'vb' | 'vc' | 'vd' | 've' | 'vf' | 'vg';
const VARIANT_ALLOWED = new Set<LandingVariant>([
  'control',
  'v2',
  'va',
  'vb',
  'vc',
  'vd',
  've',
  'vf',
  'vg',
]);

/**
 * Stamp the landing variant for this browser if not already set. Call from each
 * landing page's mount: the control homepage passes 'control', the /v2 light
 * variant passes 'v2', and each design variant passes its own id. First call
 * wins; later calls are ignored.
 */
export function stampLandingVariant(variant: LandingVariant): void {
  if (typeof window === 'undefined') return;
  if (!VARIANT_ALLOWED.has(variant)) return;
  try {
    if (!localStorage.getItem(VARIANT_KEY)) {
      localStorage.setItem(VARIANT_KEY, variant);
    }
  } catch {
    /* localStorage may be unavailable in restricted contexts. */
  }
}

/** The landing variant this browser first entered on, or undefined. */
export function getLandingVariant(): LandingVariant | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const stored = localStorage.getItem(VARIANT_KEY) as LandingVariant | null;
    return stored && VARIANT_ALLOWED.has(stored) ? stored : undefined;
  } catch {
    return undefined;
  }
}

/** Returns the current audience segment for this visitor, or undefined. */
export function getAudience(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    // URL takes precedence so an in-session click into another segment
    // re-classifies the visitor immediately.
    const url = new URL(window.location.href);
    const raw = url.searchParams.get('audience');
    if (raw && ALLOWED.has(raw)) {
      localStorage.setItem(KEY, raw);
      return raw;
    }
    const stored = localStorage.getItem(KEY);
    return stored && ALLOWED.has(stored) ? stored : undefined;
  } catch {
    return undefined;
  }
}
